import { useState, useEffect, useMemo, createContext, useContext, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandShortcut, CommandSeparator
} from "./ui/command";
import { DialogTitle } from "@radix-ui/react-dialog";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { useAuth, API } from "../App";
import { getNavForRole, quickActions as qaConfig } from "../data/navConfig";
import { LogOut, Gift, Sparkles, ShoppingBag, Wand2, Loader2 } from "lucide-react";

const isMac = typeof navigator !== "undefined" && /Mac|iPhone|iPad/.test(navigator.platform);

// === Context for programmatic open/close ===
const CommandPaletteContext = createContext({ open: () => {}, close: () => {}, toggle: () => {} });
export const useCommandPalette = () => useContext(CommandPaletteContext);

// === AI semantic search hook ===
const useSemanticSuggestions = (query, isOpen) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    if (!isOpen) { setSuggestions([]); return; }
    const trimmed = query.trim();
    // Only invoke AI for natural-language queries (>= 2 words OR 8+ chars). Skip short single keywords.
    const isPhrase = trimmed.split(/\s+/).length >= 2 || trimmed.length >= 8;
    if (!isPhrase || trimmed.length < 3) { setSuggestions([]); return; }

    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (abortRef.current) abortRef.current.abort();

    debounceRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const res = await fetch(`${API}/cmdk/search`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ query: trimmed }),
          signal: ctrl.signal,
        });
        if (res.ok) {
          const data = await res.json();
          setSuggestions(data.suggestions || []);
        }
      } catch (e) {
        if (e.name !== "AbortError") setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 500);

    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [query, isOpen]);

  return { suggestions, loading };
};

export const CommandPaletteProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { suggestions, loading } = useSemanticSuggestions(query, open);

  const close = useCallback(() => setOpen(false), []);
  const openFn = useCallback(() => setOpen(true), []);
  const toggle = useCallback(() => setOpen(p => !p), []);

  // Listen for Cmd+K / Ctrl+K
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        toggle();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [toggle]);

  // Reset query when closing
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const role = user?.role || "jobseeker";
  const nav = useMemo(() => getNavForRole(role), [role]);
  const qa = useMemo(() => qaConfig[role] || qaConfig.jobseeker, [role]);

  const go = useCallback((path, queryString) => {
    setOpen(false);
    navigate(`${path}${queryString || ""}`);
  }, [navigate]);

  const handleLogout = async () => {
    setOpen(false);
    await logout();
    navigate("/");
  };

  const ctxValue = useMemo(() => ({ open: openFn, close, toggle }), [openFn, close, toggle]);

  return (
    <CommandPaletteContext.Provider value={ctxValue}>
      {children}
      {user && (
        <CommandDialog open={open} onOpenChange={setOpen}>
          <VisuallyHidden>
            <DialogTitle>Command Palette</DialogTitle>
          </VisuallyHidden>
          <CommandInput
            placeholder={`Try “find python jobs near me”…  (${isMac ? "⌘" : "Ctrl"}K)`}
            value={query}
            onValueChange={setQuery}
            data-testid="cmdk-input"
          />
          <CommandList data-testid="cmdk-list">
            <CommandEmpty>
              {loading ? (
                <div className="flex items-center justify-center gap-2 text-muted-foreground">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>AI is thinking…</span>
                </div>
              ) : "No results found."}
            </CommandEmpty>

            {/* AI Suggestions (semantic search) */}
            {suggestions.length > 0 && (
              <>
                <CommandGroup heading={loading ? "AI Suggestions  •  Refining…" : "AI Suggestions"}>
                  {suggestions.map((s, idx) => (
                    <CommandItem
                      key={`ai-${idx}-${s.path}`}
                      value={`ai ${query} ${s.label} ${s.path}`}
                      onSelect={() => go(s.path, s.query_string)}
                      data-testid={`cmdk-ai-${idx}`}
                    >
                      <Wand2 className="text-crimson" />
                      <div className="flex flex-col flex-1 min-w-0">
                        <span className="truncate">
                          {s.label}
                          {s.query_string && <span className="text-crimson ml-1">{s.query_string}</span>}
                        </span>
                        <span className="text-xs text-muted-foreground truncate">{s.reasoning}</span>
                      </div>
                      <CommandShortcut>↵</CommandShortcut>
                    </CommandItem>
                  ))}
                </CommandGroup>
                <CommandSeparator />
              </>
            )}

            {/* Quick Actions */}
            <CommandGroup heading="Quick Actions">
              {qa.map(action => (
                <CommandItem
                  key={`qa-${action.path}`}
                  value={`quick ${action.label}`}
                  onSelect={() => go(action.path)}
                  data-testid={`cmdk-qa-${action.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  <action.icon />
                  <span>{action.label}</span>
                  <CommandShortcut>{action.path}</CommandShortcut>
                </CommandItem>
              ))}
            </CommandGroup>

            <CommandSeparator />

            {/* Navigation by category */}
            {nav.map(category => (
              <CommandGroup key={category.id} heading={category.label}>
                {category.items.map(item => (
                  <CommandItem
                    key={item.path}
                    value={`${category.label} ${item.label}`}
                    onSelect={() => go(item.path)}
                    data-testid={`cmdk-nav-${category.id}-${item.path.split("/").pop()}`}
                  >
                    <item.icon />
                    <span>{item.label}</span>
                    <CommandShortcut>{item.path}</CommandShortcut>
                  </CommandItem>
                ))}
              </CommandGroup>
            ))}

            <CommandSeparator />

            {/* Special links */}
            <CommandGroup heading="More">
              {user?.special_access?.active && user?.special_access?.type === "ambassador" && (
                <CommandItem value="ambassador hub" onSelect={() => go("/ambassador")} data-testid="cmdk-ambassador">
                  <Gift />
                  <span>Ambassador Hub</span>
                </CommandItem>
              )}
              {role === "jobseeker" && (
                <CommandItem value="upgrade subscription" onSelect={() => go("/subscription")} data-testid="cmdk-upgrade">
                  <Sparkles />
                  <span>Upgrade Plan</span>
                </CommandItem>
              )}
              {role === "recruiter" && (
                <CommandItem value="add-ons credits" onSelect={() => go("/addons")} data-testid="cmdk-addons">
                  <ShoppingBag />
                  <span>Buy Add-ons & Credits</span>
                </CommandItem>
              )}
              <CommandItem value="logout sign out" onSelect={handleLogout} data-testid="cmdk-logout">
                <LogOut />
                <span>Logout</span>
              </CommandItem>
            </CommandGroup>
          </CommandList>
        </CommandDialog>
      )}
    </CommandPaletteContext.Provider>
  );
};

// Default export kept for backwards compatibility — renders nothing on its own; provider does the work
const CommandPalette = () => null;
export default CommandPalette;
